import { useEffect, useState } from "react";
import { api, money } from "@/hub/lib/api";
import { exportInvoicePDF } from "@/hub/lib/pdf";
import { Plus, FileText, FileDown, Trash2, Share2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activities, setActivities] = useState([]);

  const load = () => api.invoices().then(setInvoices);
  useEffect(() => {
    load();
    api.groups().then(setGroups);
    api.packages().then(setPackages);
    api.activities().then(setActivities);
  }, []);

  const share = async (inv) => {
    const text = `Invoice ${inv.invoice_no} — ${inv.client_name}\nTotal: ${money(inv.total)}\nLUSH Adventure & Events Resort\n(Computer generated invoice, no signature required)`;
    if (navigator.share) {
      try { await navigator.share({ title: inv.invoice_no, text }); } catch (e) { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(text);
      toast.success("Invoice summary copied to clipboard");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500 max-w-lg">Create invoices for clients on required services, download as PDF and share. All amounts in LKR.</p>
        <InvoiceSheet groups={groups} packages={packages} activities={activities} onDone={load} />
      </div>

      <div className="bg-white border border-border">
        {invoices.length === 0 ? (
          <div className="p-12 text-center text-sm text-stone-400 border border-dashed border-stone-200 m-5">
            <FileText className="h-8 w-8 mx-auto mb-3 text-stone-300" />
            No invoices yet. Create your first client invoice.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-border">
                <th className="px-5 py-3 font-semibold">Invoice #</th>
                <th className="px-5 py-3 font-semibold">Client</th>
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Issued By</th>
                <th className="px-5 py-3 font-semibold text-right">Total</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-stone-50 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-5 py-3 font-mono font-semibold text-forest">{inv.invoice_no}</td>
                  <td className="px-5 py-3 font-medium">{inv.client_name}</td>
                  <td className="px-5 py-3 font-mono text-xs text-stone-400">{inv.date}</td>
                  <td className="px-5 py-3 text-stone-500">{inv.created_by || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono">{money(inv.total)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" data-testid={`invoice-pdf-${inv.id}`} onClick={() => exportInvoicePDF(inv)} title="Download PDF"><FileDown className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" data-testid={`invoice-share-${inv.id}`} onClick={() => share(inv)} title="Share"><Share2 className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-500 hover:bg-red-50" data-testid={`invoice-del-${inv.id}`}
                        onClick={() => { if (window.confirm("Delete invoice?")) api.deleteInvoice(inv.id).then(() => { toast.success("Deleted"); load(); }); }}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InvoiceSheet({ groups, packages, activities, onDone }) {
  const [open, setOpen] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientContact, setClientContact] = useState("");
  const [groupId, setGroupId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: 0 }]);
  const [taxPct, setTaxPct] = useState("");
  const [notes, setNotes] = useState("");
  const [quickAdd, setQuickAdd] = useState("");

  useEffect(() => {
    if (open) {
      setClientName(""); setClientContact(""); setGroupId("");
      setDate(new Date().toISOString().slice(0, 10));
      setItems([{ description: "", qty: 1, unit_price: 0 }]);
      setTaxPct(""); setNotes(""); setQuickAdd("");
    }
  }, [open]);

  const services = [
    ...packages.map((p) => ({ key: `pkg-${p.id}`, label: `Package · ${p.name}`, desc: p.name, price: p.base_price })),
    ...activities.map((a) => ({ key: `act-${a.id}`, label: `Activity · ${a.name}`, desc: a.name, price: a.cost })),
  ];

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addRow = () => setItems((arr) => [...arr, { description: "", qty: 1, unit_price: 0 }]);
  const removeRow = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const onGroup = (id) => {
    setGroupId(id);
    const g = groups.find((x) => x.id === id);
    if (g) { setClientName(g.name); setClientContact(`${g.contact_person || ""} ${g.phone || ""}`.trim()); }
  };

  const onQuick = (key) => {
    const s = services.find((x) => x.key === key);
    if (s) setItems((arr) => [...arr.filter((it) => it.description || it.unit_price), { description: s.desc, qty: 1, unit_price: s.price }]);
    setQuickAdd("");
  };

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const taxAmount = subtotal * (Number(taxPct) || 0) / 100;
  const total = subtotal + taxAmount;

  const submit = async () => {
    if (!clientName) return toast.error("Client name is required");
    const valid = items.filter((it) => it.description && Number(it.unit_price) >= 0 && it.description.trim());
    if (valid.length === 0) return toast.error("Add at least one line item");
    try {
      await api.createInvoice({
        group_id: groupId || null, client_name: clientName, client_contact: clientContact, date,
        items: valid.map((it) => ({ description: it.description, qty: Number(it.qty) || 1, unit_price: Number(it.unit_price) || 0 })),
        tax_pct: Number(taxPct) || 0, notes,
        created_by: localStorage.getItem("lush_rm_name") || (localStorage.getItem("lush_role") === "Admin" ? "Admin" : ""),
      });
      toast.success("Invoice created");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error("Could not create invoice");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="new-invoice-btn"><Plus className="h-4 w-4 mr-1" /> New Invoice</Button>
      </SheetTrigger>
      <SheetContent className="overflow-y-auto lush-scroll w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="font-heading">New Invoice</SheetTitle>
          <SheetDescription>Bill a client for required services. Amounts in LKR.</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-400">Link Client Group (optional)</Label>
            <Select value={groupId} onValueChange={onGroup}>
              <SelectTrigger className="rounded-sm" data-testid="inv-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
              <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Client Name</Label><Input value={clientName} onChange={(e) => setClientName(e.target.value)} className="rounded-sm" data-testid="inv-client-name" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Contact</Label><Input value={clientContact} onChange={(e) => setClientContact(e.target.value)} className="rounded-sm" data-testid="inv-client-contact" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm" data-testid="inv-date" /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-400">Quick Add Service</Label>
              <Select value={quickAdd} onValueChange={onQuick}>
                <SelectTrigger className="rounded-sm" data-testid="inv-quick-add"><SelectValue placeholder="Pick service" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} · {money(s.price)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-400">Line Items</Label>
            <div className="space-y-2 mt-1">
              <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-stone-400 px-1">
                <span className="col-span-6">Description</span><span className="col-span-2 text-right">Qty</span><span className="col-span-3 text-right">Unit (LKR)</span><span className="col-span-1" />
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`inv-item-${i}`}>
                  <Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="rounded-sm col-span-6 h-9" placeholder="Service" data-testid={`inv-item-desc-${i}`} />
                  <Input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="rounded-sm col-span-2 h-9 text-right" data-testid={`inv-item-qty-${i}`} />
                  <Input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="rounded-sm col-span-3 h-9 text-right font-mono" data-testid={`inv-item-price-${i}`} />
                  <button onClick={() => removeRow(i)} className="col-span-1 flex justify-center" data-testid={`inv-item-remove-${i}`}><X className="h-4 w-4 text-stone-300 hover:text-red-500" /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="rounded-sm w-full border-dashed" onClick={addRow} data-testid="inv-add-row"><Plus className="h-3.5 w-3.5 mr-1" /> Add line item</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Tax %</Label><Input type="number" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} className="rounded-sm font-mono" placeholder="0" data-testid="inv-tax" /></div>
            <div className="text-right space-y-0.5 text-sm">
              <div className="text-stone-500">Subtotal: <span className="font-mono">{money(subtotal)}</span></div>
              {Number(taxPct) > 0 && <div className="text-stone-500">Tax: <span className="font-mono">{money(taxAmount)}</span></div>}
              <div className="font-semibold text-forest">Total: <span className="font-mono" data-testid="inv-total">{money(total)}</span></div>
            </div>
          </div>

          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" rows={2} data-testid="inv-notes" /></div>
          <p className="text-[11px] italic text-stone-400">This is a computer generated invoice. No signature required.</p>
        </div>
        <SheetFooter className="mt-6">
          <Button onClick={submit} className="rounded-sm bg-forest hover:bg-forest-hover w-full" data-testid="save-invoice-btn">Create Invoice · {money(total)}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
